import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../../data/api";
import { api } from "../../../data";
import { useStaffWorkspace } from "../../../app/session";
import { loadVertragsListe } from "../vertraege/daten";
import type { BeitragLite, VertragsLage } from "./hub-model";

/**
 * Zusatzdaten des Hubs neben den Kooperationen (useKooperationen): Beiträge mit offenen Freigabe-Links und die Vertragsliste
 * mit Unterzeichnern. Je eine Abfrage für die ganze Agentur, nichts je Zeile; was eine Rolle nicht lesen darf (RLS), kommt
 * als leere Liste bzw. null zurück, dann zeigen die Pillen den Stand ohne diese Angaben.
 */

export interface HubBeitraege {
  beitraege: BeitragLite[];
  /** Beiträge mit offenem, nicht abgelaufenem Freigabe-Link bei der Marke (0073) */
  beiMarke: Set<string>;
}

export async function ladeHubBeitraege(a: Api, orgId: string, now: Date = new Date()): Promise<HubBeitraege> {
  const by = [["org_id", "eq", orgId]] as const;
  const [beitraege, links] = await Promise.all([
    a.select<BeitragLite>("deal_deliverables", { columns: "id, deal_id, period_id, status, draft_due, publish_on", filters: [...by, ["status", "neq", "cancelled"]] }).catch(() => [] as BeitragLite[]),
    a.select<{ deliverable_id: string | null; expires_at: string }>("brand_reviews", { columns: "deliverable_id, expires_at", filters: [...by, ["status", "eq", "open"]] })
      .catch(() => [] as { deliverable_id: string | null; expires_at: string }[]),
  ]);
  const jetzt = now.toISOString();
  return { beitraege, beiMarke: new Set(links.filter((l) => l.deliverable_id && l.expires_at > jetzt).map((l) => l.deliverable_id!)) };
}

export async function ladeVertragsLage(a: Api, orgId: string): Promise<VertragsLage | null> {
  try {
    const l = await loadVertragsListe(a, orgId);
    return { rows: new Map(l.contracts.map((c) => [c.id, c])), signers: l.signers, portalTalents: l.portalTalents };
  } catch {
    return null;
  }
}

/** Schlüssel beginnen mit der Tabelle, damit das Neuladen nach Aktionen (Beiträge, Verträge) auch den Hub trifft. */
export function useHubBeitraege() {
  const orgId = useStaffWorkspace().org.org_id;
  return useQuery({ queryKey: ["deal_deliverables", orgId, "hub"], queryFn: () => ladeHubBeitraege(api, orgId) });
}

export function useVertragsLage() {
  const orgId = useStaffWorkspace().org.org_id;
  return useQuery({ queryKey: ["contracts", orgId, "hub"], queryFn: () => ladeVertragsLage(api, orgId) });
}
